import { INPUT_LABEL_BLUR, PRIMARY } from "@constants/colors"
import { useAppContext } from "@contexts/app_context"
import { getCustomers } from "@domain/customer/list"
import { customerLabel, useCustomersQuery } from "@hooks/use_customers"
import useTranslate from "@hooks/use_translate"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import InputLabel from "@mui/material/InputLabel"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import { GetCustomerOwnersData, RoleTypes } from "__generated__/graphql"
import { useEffect, useId, useMemo, useState } from "react"
import {
  Controller,
  type Control,
  type FieldValues,
  type Path
} from "react-hook-form"

const MIN_SEARCH_LENGTH = 2
const SEARCH_PAGE_SIZE = 25
const NO_RECORDS: GetCustomerOwnersData[] = []

type CustomerOption = {
  id: string
  label: string
  record: GetCustomerOwnersData | null
}

type CustomerSelectProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label: string
  onSelect?: (record: GetCustomerOwnersData | null) => void
}

export default function CustomerSelect<T extends FieldValues>(
  props: CustomerSelectProps<T>
) {
  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field }) => (
        <CustomerAutocomplete
          value={(field.value as string | undefined) || ""}
          onChange={field.onChange}
          label={props.label}
          onSelect={props.onSelect}
        />
      )}
    />
  )
}

function pickedRecordKey(customerId: string) {
  return ["customer-select-picked", customerId]
}

function toOption(record: GetCustomerOwnersData): CustomerOption {
  return {
    id: record.customer?._id ?? "",
    label: customerLabel(record),
    record
  }
}

function CustomerAutocomplete(props: {
  value: string
  onChange: (value: string) => void
  label: string
  onSelect?: (record: GetCustomerOwnersData | null) => void
}) {
  const { value, onChange, label, onSelect } = props
  const { translate } = useTranslate()
  const { me } = useAppContext()
  const componentId = useId()
  const queryClient = useQueryClient()
  // api-v2 getCustomerOwners returns nothing to the USER admin role unless a search is sent.
  const canBrowse = me?.role !== RoleTypes.User

  const [isFocus, setIsFocus] = useState(false)
  const [searchText, setSearchText] = useState("")
  const debouncedSearch = useDebounce(searchText.trim(), 400)
  const search =
    debouncedSearch.length >= MIN_SEARCH_LENGTH ? debouncedSearch : ""

  const { customerRecords, isLoading: isBrowseLoading } =
    useCustomersQuery(canBrowse)

  const { data: searchRecords = NO_RECORDS, isFetching: isSearching } =
    useQuery({
      queryKey: ["customer-select-search", search],
      enabled: !!search,
      staleTime: 60 * 1000,
      queryFn: async () => {
        const response = await getCustomers({
          pageNumber: 1,
          pageSize: SEARCH_PAGE_SIZE,
          search
        })
        const docs: GetCustomerOwnersData[] = response.data.docs ?? []
        return docs.filter((item) => Boolean(item.customer?._id))
      }
    })

  const allLabel = translate("all")
  const allOption: CustomerOption = useMemo(
    () => ({ id: "", label: allLabel, record: null }),
    [allLabel]
  )

  const selectedRecord = useMemo(() => {
    if (!value) return null
    // Picks are cached so the label survives an unmount (filter popover) for USER admins,
    // who cannot re-list customers without a search.
    return (
      queryClient.getQueryData<GetCustomerOwnersData>(pickedRecordKey(value)) ??
      [...searchRecords, ...customerRecords].find(
        (item) => item.customer?._id === value
      ) ??
      null
    )
  }, [value, queryClient, searchRecords, customerRecords])

  // MUI resets the typed text whenever `value` changes identity, so this must not be rebuilt per render.
  const selected: CustomerOption = useMemo(() => {
    if (!value) return allOption
    return selectedRecord
      ? toOption(selectedRecord)
      : { id: value, label: value, record: null }
  }, [value, selectedRecord, allOption])

  useEffect(() => {
    if (!value || selected.record) onSelect?.(selected.record)
  }, [value, selected.record, onSelect])

  const options = useMemo(() => {
    let records: GetCustomerOwnersData[] = []
    if (search) {
      records = searchRecords
    } else if (canBrowse) {
      records = customerRecords.filter(
        (item) => item.customer?.appStatus === "Enabled"
      )
    }
    const byId = new Map<string, CustomerOption>([[allOption.id, allOption]])
    if (selected.id) byId.set(selected.id, selected)
    records.forEach((record) => {
      const option = toOption(record)
      if (!byId.has(option.id)) byId.set(option.id, option)
    })
    return Array.from(byId.values())
  }, [search, searchRecords, canBrowse, customerRecords, allOption, selected])

  const needsMoreInput =
    !canBrowse && searchText.trim().length < MIN_SEARCH_LENGTH

  return (
    <Box sx={{ width: "100%" }}>
      <InputLabel
        sx={{
          display: "flex",
          flexDirection: "row",
          color: isFocus ? PRIMARY : INPUT_LABEL_BLUR
        }}
        htmlFor={componentId}
      >
        <Typography>{label}</Typography>
      </InputLabel>
      <Autocomplete<CustomerOption, false, true>
        id={componentId}
        options={options}
        value={selected}
        disableClearable
        loading={isSearching || (canBrowse && isBrowseLoading)}
        loadingText={translate("customer_search_loading")}
        noOptionsText={
          needsMoreInput
            ? translate("customer_search_min_chars")
            : translate("no_options")
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        filterOptions={(items) => {
          if (search) return items
          const query = searchText.trim().toLocaleLowerCase("tr")
          if (!query) return items
          return items.filter((item) =>
            item.label.toLocaleLowerCase("tr").includes(query)
          )
        }}
        renderOption={(liProps, option) => (
          <li {...liProps} key={option.id || "all"}>
            {option.label}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={translate("customer_search_placeholder")}
          />
        )}
        onChange={(_, option) => {
          if (option.record) {
            queryClient.setQueryData(pickedRecordKey(option.id), option.record)
          }
          setSearchText("")
          onChange(option.id)
        }}
        onInputChange={(_, text, reason) => {
          if (reason === "input") setSearchText(text)
        }}
        onFocus={() => setIsFocus(true)}
        onBlur={() => {
          setIsFocus(false)
          setSearchText("")
        }}
        sx={{
          "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
            pt: 0,
            pl: 0,
            pb: 0
          },
          " input": {
            padding: "2px 4px!important"
          }
        }}
      />
    </Box>
  )
}
